import React, { useEffect, useState } from "react";
import { getISTDateOnlyTimestamp } from "../utils/dateTime";

const BASE_URL = "/v1";

function FollowUpConsultation({ onViewHistory, clinic, onError }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  useEffect(() => {
    fetchFollowUps();
  }, []);

  const handleCompleteFollowUp = async (appointmentId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/appointments/${appointmentId}/followup`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token
        },
        body: JSON.stringify({
          followUp: true
        })
      });

      if (!res.ok) {
        throw new Error("Failed to complete follow-up");
      }

      setData((prev) =>
        prev.map((item) =>
          item.appointmentId === appointmentId
            ? { ...item, followUp: true }
            : item
        )
      );
    } catch (err) {
      console.error(err);
      setMessage("Failed to update follow-up");
      setMessageType("danger");
    }
  };

  const getFollowUpProgress = (appointmentDate) => {
    const today = getISTDateOnlyTimestamp(new Date());
    const apptDate = getISTDateOnlyTimestamp(appointmentDate);
    const diffTime = apptDate === null ? 0 : today - apptDate;
    const daysPassed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const daysLeft = 7 - daysPassed;
    const progress = (daysLeft / 7) * 100;

    return { daysPassed, daysLeft, progress };
  };

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      setMessage("");
      setMessageType("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/appointments/followups`, {
        headers: {
          Authorization: "Bearer " + token
        }
      });

      const result = await res.json();
      setData(result || []);
    } catch (err) {
      console.error("Follow-up fetch failed", err);
      setMessage("Failed to load follow-ups");
      setMessageType("danger");
      onError?.("danger", "Cannot load follow-up consultations right now. Please check the backend server.");
    } finally {
      setLoading(false);
    }
  };

  if (!loading && data.length === 0) return null;

  return (
    <section className="followup-panel mb-3">
      <div
        className="followup-header d-flex justify-content-between align-items-center"
        onClick={() => setOpen(!open)}
      >
        <strong>Follow-up Consultations ({data.length})</strong>

        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-dark"
            onClick={(e) => {
              e.stopPropagation();
              fetchFollowUps();
            }}
          >
            Refresh
          </button>

          <span>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div className="followup-body">
          {message && (
            <div className={`alert alert-${messageType || "info"} py-2`}>
              {message}
            </div>
          )}

          {loading ? (
            <div className="text-center p-2">
              <div className="spinner-border" role="status"></div>
              <div className="mt-2">Loading...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0 followup-table">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ whiteSpace: "nowrap" }}>Owner Mobile</th>
                    <th style={{ whiteSpace: "nowrap" }}>Pet Details</th>
                    <th style={{ whiteSpace: "nowrap" }}>Visit Reason</th>
                    <th style={{ whiteSpace: "nowrap" }}>Visited On</th>
                    <th style={{ whiteSpace: "nowrap" }}>Action</th>
                    <th style={{ whiteSpace: "nowrap" }}>Status</th>
                    <th style={{ whiteSpace: "nowrap" }}>Follow-up Window</th>
                  </tr>
                </thead>

                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No follow-ups found
                      </td>
                    </tr>
                  ) : (
                    data.map((item, idx) => {
                      const petName = (item.petName ?? "").trim();
                      const followupMeta = getFollowUpProgress(item.appointmentDate);
                      const whatsappMessage = [
                        "*Hello, Pet Parent!*",
                        `A quick follow-up about *${petName}* after the recent consultation.`,
                        `*Visit reason:* ${item.reason}`,
                        `How is *${petName}* feeling? Please share a quick update when convenient.`,
                        `Wishing *${petName}* a speedy recovery!`,
                        [
                          clinic?.name,
                          clinic?.phone && `📞 ${clinic.phone}`
                        ].filter(Boolean).join("\n")
                      ].filter(Boolean).join("\n\n");

                      return (
                        <tr key={item.id || idx}>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <a
                              href={`https://wa.me/91${item.ownerMobile}?text=${encodeURIComponent(whatsappMessage)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-success fw-bold text-decoration-none"
                              title={item.ownerMobile}
                            >
                              WhatsApp
                            </a>
                          </td>

                          <td style={{ whiteSpace: "nowrap", maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <div className="fw-bold text-truncate" title={petName}>{petName}</div>
                            <div className="text-muted small text-truncate" title={`${item.petType} • ${item.breed} • ${item.age} Y`}>
                              {item.petType} • {item.breed} • {item.age} Y
                            </div>
                          </td>

                          <td style={{ whiteSpace: "nowrap", maxWidth: "320px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <span title={item.reason}>{item.reason}</span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <span title={item.appointmentDate}>{item.appointmentDate}</span>
                          </td>

                          <td style={{ whiteSpace: "nowrap" }}>
                            <div className="d-flex gap-2 flex-nowrap align-items-center">
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() =>
                                  onViewHistory({
                                    id: item.petId,
                                    name: petName
                                  })
                                }
                              >
                                View
                              </button>

                              {!item.followUp && (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() =>
                                    handleCompleteFollowUp(item.appointmentId)
                                  }
                                >
                                  Complete
                                </button>
                              )}
                            </div>
                          </td>

                          <td style={{ whiteSpace: "nowrap" }}>
                            {item.followUp ? (
                              <span className="badge bg-success">Completed</span>
                            ) : (
                              <span className="badge bg-warning text-dark">In Progress</span>
                            )}
                          </td>

                          <td style={{ minWidth: "180px", whiteSpace: "nowrap" }}>
                            <div className="small fw-semibold mb-1">
                              {followupMeta.daysLeft <= 1 ? (
                                <span className="text-danger">⚠ Last Day</span>
                              ) : followupMeta.daysLeft <= 3 ? (
                                <span className="text-warning">⏳ {followupMeta.daysLeft} Days Left</span>
                              ) : (
                                <span className="text-success">✅ {followupMeta.daysLeft} Days Left</span>
                              )}
                            </div>

                            <div
                              className="progress"
                              style={{
                                height: "8px",
                                borderRadius: "10px"
                              }}
                            >
                              <div
                                className={`progress-bar ${followupMeta.daysLeft <= 1
                                    ? "bg-danger"
                                    : followupMeta.daysLeft <= 3
                                      ? "bg-warning"
                                      : "bg-success"
                                  }`}
                                role="progressbar"
                                style={{
                                  width: `${followupMeta.progress}%`
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default FollowUpConsultation;
